import React, { useState, useEffect, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import ChatBox from "./components/chatbox/ChatBox";
import Loader from "./components/Loader";
import AdmissionModal from "./components/AdmissionModal";

// Pages
import Home from "./pages/Home";
import Blog from "./pages/Blog";
import Gallery from "./pages/Gallery";
import VioletBlock from "./pages/VioletBlock";
import Contact from "./pages/Contact";

// Scroll to top on route change
const ScrollToTopOnRouteChange = React.memo(() => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
});

function App() {
  const [loading, setLoading] = useState(true);
  const [chatbot, setChatbot] = useState({
    isOpen: false,
    hovered: false,
    showPopup: false,
    popupShownOnce: false,
  });
  const [showAdmissionModal, setShowAdmissionModal] = useState(false);

  const shashaImageUrl =
    "https://res.cloudinary.com/ddvpczzng/image/upload/v1757446085/shasha_logo_tilt_yzbte7.png";

  // Simulate loading + show admission modal
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
      setShowAdmissionModal(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleCloseAdmissionModal = useCallback(() => {
    setShowAdmissionModal(false);
    setChatbot((prev) => {
      if (!prev.popupShownOnce) {
        setTimeout(() => {
          setChatbot((p) => ({ ...p, showPopup: false }));
        }, 4000);
        return { ...prev, showPopup: true, popupShownOnce: true };
      }
      return prev;
    });
  }, []);

  const handleImageError = (e) => {
    e.currentTarget.src = "";
    e.currentTarget.alt = "Shasha Assistant";
    e.currentTarget.classList.add("bg-blue-500");
  };

  useEffect(() => {
    // Inject structured data for sitelinks navigation (helps Google discover structure)
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "St. Thomas Vidyalayam Matric Higher Secondary School",
      "url": "https://www.stvmhss.in/",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://www.stvmhss.in/search?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  if (loading) return <Loader />;

  return (
    <Router>
      <ScrollToTopOnRouteChange />
      <div className="flex flex-col min-h-screen bg-gradient-to-br from-gray-100 via-white to-gray-400
 overflow-x-hidden">
        <Navbar />

        {/* Main Content */}
        <main className="flex-grow [&>*]:m-0 [&>*]:p-0">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/violetblock" element={<VioletBlock />} />
            <Route path="/contact" element={<Contact />} />
          </Routes>
        </main>

        <Footer />
        <ScrollToTop />

        {/* Admission Modal */}
        <AdmissionModal
          isVisible={showAdmissionModal}
          onClose={handleCloseAdmissionModal}
        />

        {/* Chatbot Trigger */}
        {!showAdmissionModal && (
          <div className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 z-50 flex items-end gap-2">
            {(chatbot.showPopup || (chatbot.hovered && !chatbot.isOpen)) && (
              <div className="bg-white text-xs sm:text-sm rounded-lg sm:rounded-xl shadow-lg p-2 sm:p-3 animate-fade-in border max-w-[180px] sm:max-w-xs">
                <p>
                  <span className="font-semibold">Hi! 👋</span>
                  <br />
                  I'm Shasha, <br /> Our School assistant
                </p>
              </div>
            )}
            <button
              onMouseEnter={() =>
                setChatbot((prev) => ({ ...prev, hovered: true }))
              }
              onMouseLeave={() =>
                setChatbot((prev) => ({ ...prev, hovered: false }))
              }
              onClick={() =>
                setChatbot((prev) => ({
                  ...prev,
                  isOpen: !prev.isOpen,
                  showPopup: false,
                }))
              }
              className="rounded-full w-12 h-12 sm:w-14 sm:h-14 bg-white shadow-lg hover:scale-105 transition-transform overflow-hidden"
              aria-label="Open School Assistant"
            >
              <img
                src={shashaImageUrl}
                alt="Shasha - School Assistant"
                className="w-full h-full object-cover transform scale-110"
                loading="lazy"
                onError={handleImageError}
              />
            </button>
          </div>
        )}

        {/* ChatBox */}
        <ChatBox
          isActive={chatbot.isOpen}
          toggle={() => setChatbot((prev) => ({ ...prev, isOpen: false }))}
        />
      </div>
    </Router>
  );
}

export default App;
